import { Component } from '@theme/component';

/**
 * Validación de RUT chileno (módulo 11). Acepta "76.123.456-7", "761234567" o "76123456-K".
 * @param {string} value
 * @returns {boolean}
 */
export function validarRut(value) {
  const clean = String(value || '').replace(/[^0-9kK]/g, '').toUpperCase();
  if (clean.length < 8) return false;
  const cuerpo = clean.slice(0, -1);
  const dv = clean.slice(-1);
  let suma = 0;
  let mult = 2;
  for (let i = cuerpo.length - 1; i >= 0; i--) {
    suma += Number(cuerpo[i]) * mult;
    mult = mult === 7 ? 2 : mult + 1;
  }
  const resto = 11 - (suma % 11);
  const dvEsperado = resto === 11 ? '0' : resto === 10 ? 'K' : String(resto);
  return dv === dvEsperado;
}

/**
 * Formatea el RUT como 76.123.456-7
 * @param {string} value
 */
export function formatearRut(value) {
  const clean = String(value || '').replace(/[^0-9kK]/g, '').toUpperCase();
  if (clean.length < 2) return clean;
  const cuerpo = clean.slice(0, -1);
  const dv = clean.slice(-1);
  return `${cuerpo.replace(/\B(?=(\d{3})+(?!\d))/g, '.')}-${dv}`;
}

/**
 * Bloque Boleta / Factura del carro. El RUT es obligatorio siempre (boleta y factura).
 * Bloque Boleta / Factura del carro. Persiste los atributos vía /cart/update.js
 * para que lleguen al checkout aunque el cliente no vuelva a enviar el formulario.
 */
class CartFactura extends Component {
  requiredRefs = ['fields'];

  connectedCallback() {
    super.connectedCallback();
    this.#syncVisibility();
    this.refs.rut?.addEventListener('blur', this.#onRutBlur);
    this.refs.rut?.addEventListener('change', this.#persist);
    this.refs.fields.querySelectorAll('input').forEach((input) => input.addEventListener('change', this.#persist));
    // Botón Pagar del mismo resumen (página del carro o carro lateral); en fase de captura para frenar el envío
    document.addEventListener('click', this.#onCheckout, true);
    // Envío del formulario por otra vía (Enter en un campo, requestSubmit): mismo control si va al pago
    document.addEventListener('submit', this.#onSubmit, true);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('click', this.#onCheckout, true);
    document.removeEventListener('submit', this.#onSubmit, true);
  }

  onTipoChange = () => {
    this.#syncVisibility();
    this.#persist();
  };

  #syncVisibility() {
    const factura = this.querySelector('input[value="factura"]')?.checked;
    this.refs.fields.hidden = !factura;
    this.dataset.tipo = factura ? 'factura' : 'boleta';
    this.refs.fields.querySelectorAll('input').forEach((input) => {
      if (input.name.includes('direccion')) return;
      input.required = Boolean(factura);
    });
  }

  #onRutBlur = () => {
    const input = this.refs.rut;
    if (!input) return;
    if (input.value.trim() === '') {
      this.refs.rutError.hidden = true;
      return;
    }
    const ok = validarRut(input.value);
    if (ok) input.value = formatearRut(input.value);
    this.refs.rutError.hidden = ok;
    input.closest('.dt-form__field')?.toggleAttribute('data-invalid', !ok);
  };

  #persist = async () => {
    const rutValor = this.refs.rut?.value ?? '';
    const attributes = {
      tipo_documento: this.dataset.tipo,
      rut: validarRut(rutValor) ? formatearRut(rutValor) : rutValor,
      razon_social: this.querySelector('input[name="attributes[razon_social]"]')?.value ?? '',
      giro: this.querySelector('input[name="attributes[giro]"]')?.value ?? '',
      direccion_facturacion: this.querySelector('input[name="attributes[direccion_facturacion]"]')?.value ?? '',
    };
    try {
      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ attributes }),
      });
    } catch (e) {
      console.warn('[dintass] No se pudieron guardar los atributos de factura', e);
    }
  };

  #onCheckout = (event) => {
    const boton = event.target.closest?.('#checkout, button[name="checkout"]');
    if (!boton || !this.isConnected) return;
    this.#validar(boton, event);
  };

  #onSubmit = (event) => {
    const boton = event.submitter;
    if (!boton?.matches?.('#checkout, button[name="checkout"]') || !this.isConnected) return;
    this.#validar(boton, event);
  };

  /** @param {Element} boton @param {Event} event */
  #validar(boton, event) {
    // solo el bloque que está en el mismo resumen que el botón (carro lateral vs página del carro)
    const resumen = boton.closest('.cart-summary, .cart-drawer__summary, .cart-page__summary, form, cart-drawer-component') || document;
    if (!resumen.contains(this) && resumen !== document) return;
    // RUT obligatorio para boleta y factura; razón social y giro solo para factura
    const rut = this.refs.rut;
    const invalid = !validarRut(rut?.value ?? '');
    const missing = this.dataset.tipo === 'factura' ? [...this.refs.fields.querySelectorAll('input[required]')].filter((i) => !i.value.trim()) : [];
    if (invalid || missing.length) {
      event.preventDefault();
      event.stopImmediatePropagation();
      this.refs.rutError.hidden = !invalid;
      if (invalid) rut?.closest('.dt-form__field')?.setAttribute('data-invalid', '');
      (invalid ? rut : missing[0])?.focus();
      (invalid ? rut : missing[0])?.scrollIntoView({ block: 'center', behavior: 'smooth' });
      missing.forEach((i) => i.closest('.dt-form__field')?.setAttribute('data-invalid', ''));
    }
  }
}

if (!customElements.get('cart-factura')) customElements.define('cart-factura', CartFactura);
